"use client";

import { useState } from "react";
import { Download, ArrowRight, FileText, CheckCircle2, Rocket } from "lucide-react";
import { getAccessKey } from "@/app/actions";
import { trackEvent } from "@/lib/analytics";

export interface LeadMagnetCardProps {
  magnetId?: string;
  variant?: "card" | "banner" | "compact" | "inline";
  title?: string;
  description?: string;
  onOpenModal?: (magnetId: string) => void;
  className?: string;
}

const magnetConfigs: Record<string, {
  defaultTitle: string;
  defaultDesc: string;
  icon: string;
  badge: string;
  highlights: string[];
  fileName: string;
  downloadUrl: string;
}> = {
  "tech-audit-checklist": {
    defaultTitle: "Technical Audit Checklist",
    defaultDesc: "A 50-point diagnostic checklist for auditing fullstack architecture, edge scalability, database performance, CI/CD safety, and cloud security.",
    icon: "file",
    badge: "FREE GUIDE",
    highlights: ["Core Web Vitals Audit", "Security & Auth Review", "Database Optimization"],
    fileName: "52s-tech-audit-checklist.html",
    downloadUrl: "/downloads/52s-tech-audit-checklist.html",
  },
  "startup-launch-guide": {
    defaultTitle: "Startup Launch Readiness Guide",
    defaultDesc: "The 4-week founder blueprint to go from an MVP concept to a scalable production environment.",
    icon: "startup",
    badge: "FOUNDER BLUEPRINT",
    highlights: ["Architecture Decisions", "FinOps & Cost Strategy", "Due Diligence Prep"],
    fileName: "52s-startup-launch-readiness-guide.html",
    downloadUrl: "/downloads/52s-startup-launch-readiness-guide.html",
  }
};

export default function LeadMagnetCard({
  magnetId = "tech-audit-checklist",
  variant = "card",
  title,
  description,
  onOpenModal,
  className = "",
}: LeadMagnetCardProps) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const config = magnetConfigs[magnetId] || magnetConfigs["tech-audit-checklist"];
  
  const displayTitle = title || config.defaultTitle;
  const displayDesc = description || config.defaultDesc;

  const handleDirectSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const accessKey = await getAccessKey();
      if (accessKey) {
        const res = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
          body: JSON.stringify({
            access_key: accessKey,
            email: email,
            subject: `Lead Magnet Download: ${magnetId}`,
            from_name: "52S Solutions Website",
          }),
        });

        if (!res.ok) {
          throw new Error("Failed to submit");
        }
      }

      setDownloadReady(true);
      trackEvent("lead_magnet_downloaded", {
        asset_id: magnetId,
        source: variant === "banner" ? "blog_banner" : "card",
      });

      const w = typeof window !== "undefined" ? (window as Window & { dataLayer?: Array<Record<string, unknown>> }) : null;
      if (w?.dataLayer) {
        w.dataLayer.push({
          event: "lead_magnet_downloaded",
          magnetId,
        });
      }
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Banner Variant (wide, good for blog posts)
  if (variant === "banner") {
    return (
      <div
        className={`p-6 md:p-8 rounded-2xl bg-gradient-to-r from-purple-100/70 via-slate-100/90 to-purple-100/70 dark:from-purple-950/40 dark:via-slate-900/60 dark:to-purple-950/40 border border-purple-200 dark:border-purple-500/20 backdrop-blur-xl flex flex-col md:flex-row items-center gap-6 shadow-sm dark:shadow-none ${className}`}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700 border border-purple-300 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30">
              {config.badge}
            </span>
          </div>
          <h3 className="font-mono text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-2">{displayTitle}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xl mb-4">{displayDesc}</p>
        </div>
        
        <div className="w-full md:w-auto min-w-[280px]">
          {!downloadReady ? (
            <form onSubmit={handleDirectSubmit} className="flex flex-col gap-3">
              <input type="checkbox" name="botcheck" className="hidden" style={{ display: "none" }} />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/[0.04] border border-slate-300 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all shadow-inner"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 rounded-xl font-mono font-bold text-sm uppercase bg-purple-600 hover:bg-purple-500 text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(139,92,246,0.3)]"
              >
                {isSubmitting ? "SENDING..." : "GET FREE ACCESS"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <div className="flex flex-col gap-3">
              <a
                href={config.downloadUrl}
                target="_blank"
                rel="noopener"
                className="w-full px-6 py-3 rounded-xl font-mono font-bold text-sm uppercase bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)]"
              >
                <Download className="w-4 h-4" />
                OPEN GUIDE
              </a>
            </div>
          )}
          {errorMessage && (
            <p className="text-red-500 dark:text-red-400 text-xs mt-2 text-center">{errorMessage}</p>
          )}
        </div>
      </div>
    );
  }

  // Default Card Variant
  return (
    <div
      className={`p-6 rounded-2xl bg-slate-50/80 dark:bg-white/[0.02] border border-slate-200/90 dark:border-white/[0.08] hover:border-purple-400 dark:hover:border-purple-500/40 shadow-sm dark:shadow-none hover:shadow-md transition-all flex flex-col justify-between group backdrop-blur-xl ${className}`}
    >
      <div>
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700 border border-purple-300 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30">
            {config.badge}
          </span>
          <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 border border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
            {config.icon === "startup" ? <Rocket className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
          </div>
        </div>

        <h3 className="text-lg md:text-xl font-bold font-mono text-slate-900 dark:text-white mb-2">{displayTitle}</h3>
        <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 mb-5 leading-relaxed">{displayDesc}</p>

        <div className="space-y-2 mb-6">
          {config.highlights.map((point: string, idx: number) => (
            <div key={idx} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
              <CheckCircle2 className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
              <span>{point}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4 border-t border-slate-200 dark:border-white/[0.06]">
        {!downloadReady ? (
          <button
            type="button"
            onClick={() => onOpenModal && onOpenModal(magnetId)}
            className="w-full py-3 rounded-xl font-mono font-bold text-xs uppercase tracking-wider bg-purple-50 hover:bg-purple-600 text-purple-700 hover:text-white border border-purple-200 hover:border-purple-600 dark:bg-purple-600/20 dark:hover:bg-purple-600 dark:text-purple-300 dark:hover:text-white dark:border-purple-500/30 dark:hover:border-purple-500 flex items-center justify-center gap-2 transition-all"
          >
            <span>ACCESS FREE GUIDE</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <a
            href={config.downloadUrl}
            target="_blank"
            rel="noopener"
            className="w-full py-3 rounded-xl font-mono font-bold text-xs uppercase bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 transition-all shadow"
          >
            <Download className="w-4 h-4" />
            OPEN GUIDE
          </a>
        )}
      </div>
    </div>
  );
}
